<template>
	<div ref="media-section" class="media-section">
		<div class="title">
			<span>{{ $t('main.details-tabs.media') }}</span>
			<div v-if="pageList.length > 1" class="nav">
				<span>{{ selectedPageIndex + 1 }} / {{ pageList.length }}</span>
				<div class="nav-dots">
					<div
						v-for="i in pageList.length"
						:key="i"
						class="dot"
						:class="{ selected: i - 1 === selectedPageIndex }"
						@click="setPageIndex(i - 1, true)"
					></div>
				</div>
				<div v-if="!isMobile" class="arrow previous" :class="{ disabled: !canNavPrevious }" @click="navPreviousPage"></div>
				<div v-if="!isMobile" class="arrow next" :class="{ disabled: !canNavNext }" @click="navNextPage"></div>
			</div>
		</div>
		<div v-if="pageList.length > 0" ref="media" class="media-wrapper">
			<transition :name="`media-${transitionName}`" tag="div" mode="out-in">
				<div :key="selectedPageIndex" class="pages">
					<div class="section" :class="{ ieMode: isIE }">
						<template v-for="(page, $index) in pageList[selectedPageIndex]">
							<div
								v-if="selectedPageIndex === 0 ? $index < 1 : $index < 4"
								:key="$index"
								class="preview"
								:class="{ first: selectedPageIndex === 0, mobile: isMobile }"
								@mouseenter="hovered = !isMobile ? $index : -1"
								@mouseleave="hovered = -1"
								@click="showPreview(page.index)"
							>
								<img v-if="page.Type === 'image'" :src="`${cdnHost}${getLocaleString(page.Image)}`" />
								<img
									v-if="page.Type === 'youtube'"
									:src="`https://img.youtube.com/vi/${getLocaleString(page.Image)}/0.jpg`"
								/>
								<svgIcon
									v-if="page.Type === 'youtube' && hovered !== $index"
									icon-holder-class="play"
									icon-id="icon-btn-play"
								/>
								<svgIcon
									v-if="page.Type === 'youtube' && hovered === $index"
									icon-holder-class="play"
									icon-id="icon-btn-play-hover"
								/>
							</div>
						</template>
					</div>
					<div
						v-if="(selectedPageIndex === 0 && pageList[selectedPageIndex].length > 1) || selectedPageIndex !== 0"
						:class="{ ieMode: isIE }"
						class="section"
					>
						<template v-for="(page, $index) in pageList[selectedPageIndex]">
							<div
								v-if="selectedPageIndex === 0 ? $index > 0 : $index > 3"
								:key="$index"
								class="preview"
								:class="{ mobile: isMobile }"
								@mouseenter="hovered = !isMobile ? $index : -1"
								@mouseleave="hovered = -1"
								@click="showPreview(page.index)"
							>
								<img v-if="page.Type === 'image'" :src="`${cdnHost}${getLocaleString(page.Image)}`" />
								<img
									v-if="page.Type === 'youtube'"
									:src="`https://img.youtube.com/vi/${getLocaleString(page.Image)}/0.jpg`"
								/>
								<svgIcon
									v-if="page.Type === 'youtube' && hovered !== $index"
									icon-holder-class="play"
									icon-id="icon-btn-play"
								/>
								<svgIcon
									v-if="page.Type === 'youtube' && hovered === $index"
									icon-holder-class="play"
									icon-id="icon-btn-play-hover"
								/>
							</div>
						</template>
					</div>
				</div>
			</transition>
		</div>
		<Lightbox
			v-if="showLightbox"
			:media-list="processedMediaList"
			:selected-preview-index="selectedPreviewIndex"
			@setPageIndex="setPageIndex"
			@track="track"
			@close="closeLightbox"
		></Lightbox>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { getLocaleString } from '../../../plugins/helpers';
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import Lightbox from './lightbox';

export default {
	name: 'GameDetailsMediaSection',
	components: {
		svgIcon,
		Lightbox,
	},
	props: {
		mediaList: {
			type: Array,
			default: () => [],
		},
		game: {
			type: Object,
			default: () => {},
		},
	},
	data() {
		return {
			processedMediaList: [],
			pageList: [],
			previousSelectedPageIndex: 0,
			selectedPageIndex: 0,
			xDown: null,
			yDown: null,
			hovered: -1,
			showLightbox: false,
			selectedPreviewIndex: -1,
		};
	},
	computed: {
		...mapState(['isIOS', 'isAndroid', 'isIE']),
		canNavNext() {
			return this.selectedPageIndex < this.pageList.length - 1;
		},
		canNavPrevious() {
			return this.selectedPageIndex > 0;
		},
		transitionName() {
			return this.selectedPageIndex >= this.previousSelectedPageIndex ? 'next' : 'previous';
		},
		isMobile() {
			return this.isIOS || this.isAndroid;
		},
		language() {
			return this.$i18n.locale;
		},
	},
	mounted() {
		this.$emit('setRef', 'media', this.$refs['media-section']);
		this.setPageList();

		this.$nextTick(() => {
			this.$refs.media.addEventListener('touchstart', this.handleTouchStart, false);
			this.$refs.media.addEventListener('touchmove', this.handleTouchMove, false);
		});
	},
	methods: {
		getLocaleString(data) {
			return getLocaleString(data, this.language);
		},
		setPageIndex(index, jump) {
			this.previousSelectedPageIndex = this.selectedPageIndex;
			this.selectedPageIndex = index;
			if (jump) this.track('Jump');
		},
		navNextPage() {
			if (!this.canNavNext) return;
			this.track('Right');
			this.setPageIndex(this.selectedPageIndex + 1);
		},
		navPreviousPage() {
			if (!this.canNavPrevious) return;
			this.track('Left');
			this.setPageIndex(this.selectedPageIndex - 1);
		},
		handleTouchStart(e) {
			const { clientX, clientY } = e.touches[0];
			this.xDown = clientX;
			this.yDown = clientY;
		},
		handleTouchMove(e) {
			if (!this.xDown || !this.yDown || this.pageList.length <= 1) return;

			let xUp = e.touches[0].clientX;
			let yUp = e.touches[0].clientY;

			let xDiff = this.xDown - xUp;
			let yDiff = this.yDown - yUp;

			if (Math.abs(xDiff) > Math.abs(yDiff)) {
				if (xDiff > 0) {
					this.navNextPage();
				} else {
					this.navPreviousPage();
				}
			}

			this.xDown = null;
			this.yDown = null;
		},
		showPreview(index) {
			this.selectedPreviewIndex = index;
			this.showLightbox = true;
			this.track('Open Lightbox');
		},
		closeLightbox() {
			this.showLightbox = false;
			this.track('Close Lightbox');
		},
		setPageList() {
			const len = this.mediaList.length;
			const breakpoints = [0, 5, 13, 21, 29, 37, 45, 53, 61, 69];
			let lastPageIndex = 0;
			for (let i = 0; i < len; i++) {
				const media = this.mediaList[i];
				const pageIndex = breakpoints.indexOf(i);
				if (pageIndex > -1) {
					lastPageIndex = pageIndex;
					this.pageList.push([]);
				}
				media.index = i;
				media.pageIndex = lastPageIndex;
				this.processedMediaList.push(media);
				this.pageList[lastPageIndex].push(media);
			}
		},
		track(gameDetailMediaInteraction) {
			dataLayer.push({
				event: 'Game Detail Media Interaction',
				gameDetailMediaInteraction,
				gameDetailName: this.game.name,
				gameDetailID: this.game.productID,
			});
		},
	},
};
</script>

<style lang="less" scoped>
.media-previous-enter-active,
.media-next-enter-active {
	transition: all 1s;
}
.media-previous-enter {
	opacity: 0;
	transform: translateX(-50px);
}
.media-next-enter {
	opacity: 0;
	transform: translateX(50px);
}
.media-previous-enter-to,
.media-next-enter-to {
	opacity: 1;
}

.media-section {
	height: 100%;
	margin-bottom: 50px;
	padding: 0 20px;
	width: 100%;
	.title {
		align-items: center;
		color: white;
		display: flex;
		font-size: 20px;
		height: 40px;
		justify-content: space-between;
		letter-spacing: -0.4px;
		margin-bottom: 15px;
		user-select: none;
		.nav {
			align-items: center;
			color: rgba(255, 255, 255, 0.5);
			display: flex;
			font-size: 16px;
			.nav-dots {
				display: none;
			}
			span {
				margin-right: 6px;
			}
			.arrow {
				background-color: rgba(0, 0, 0, 0.5);
				border: 1px solid rgba(255, 255, 255, 0.2);
				border-radius: 5px;
				cursor: pointer;
				height: 40px;
				position: relative;
				transition: all 0.2s;
				width: 31px;
				&.disabled {
					cursor: default;
					&:before {
						opacity: 0.2;
					}
				}
				&:not(:active):not(.disabled):hover {
					background-color: rgba(0, 0, 0, 1);
					border: 1px solid rgba(255, 255, 255, 0.7);
					&:before {
						opacity: 1;
					}
				}
				&:active:not(.disabled) {
					&:before {
						opacity: 0.5;
					}
				}
				&:before {
					background-position: center;
					background-repeat: no-repeat;
					content: '';
					height: 100%;
					opacity: 0.8;
					position: absolute;
					width: 100%;
				}
				&.previous {
					&:before {
						background-image: url('~@/images/game-details/arrows/previous.svg');
					}
					margin: 0 2px 0 0;
				}
				&.next {
					&:before {
						background-image: url('~@/images/game-details/arrows/next.svg');
					}
				}
			}
		}
	}
	.media-wrapper {
		min-height: 210px;
	}
	.pages {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		margin: auto;
		width: 335px;
		.section {
			align-items: flex-start;
			display: flex;
			flex-wrap: wrap;
			height: 200px;
			justify-content: space-between;
			margin-bottom: 10px;
			width: 100%;
			&.ieMode {
				.play {
					margin-left: -29px;
					margin-top: -29px;
				}
			}
			.preview {
				align-items: center;
				border-radius: 5px;
				cursor: pointer;
				display: flex;
				height: 95px;
				justify-content: center;
				margin-bottom: 10px;
				overflow: hidden;
				position: relative;
				width: 162px;
				&:not(.mobile):hover {
					border: 4px solid #0077ff;
					img {
						height: calc(100% + 8px);
						left: -4px;
						top: -4px;
						width: calc(100% + 8px);
					}
				}
				.play {
					height: 58px;
					position: absolute;
					width: 58px;
				}
				img {
					height: 100%;
					left: 0;
					position: absolute;
					top: 0;
					width: 100%;
				}
				&.first {
					height: 100%;
					width: 100%;
				}
			}
		}
	}
}

@media (min-width: 768px) {
	.media-section {
		margin-bottom: 50px;
		.title {
			margin: 0 auto 10px;
			.nav {
				.nav-dots {
					align-items: center;
					display: flex;
					height: 100%;
					justify-content: center;
					margin-right: 20px;
					.dot {
						background-color: white;
						cursor: pointer;
						height: 6px;
						opacity: 0.5;
						transition: opacity 0.2s;
						width: 6px;
						&:not(:last-child) {
							margin-right: 7px;
						}
						&.selected,
						&:hover {
							opacity: 1;
						}
					}
				}
				span {
					display: none;
				}
			}
		}
		.pages {
			flex-direction: row;
			height: 210px;
			width: 728px;
			.section {
				height: 210px;
				width: 359px;
				.preview {
					height: 100px;
					width: 174px;
				}
			}
		}
	}
}

@media (min-width: 1024px) {
	.media-section {
		.title {
			font-size: 16px;
			height: 75px;
			letter-spacing: -0.32px;
			padding-top: 35px;
			width: 770px;
		}
		.media-wrapper {
			min-height: 222px;
		}
		.pages {
			height: 222px;
			width: 770px;
			.section {
				height: 222px;
				width: 380px;
				.preview {
					height: 106px;
					width: 185px;
				}
			}
		}
	}
}

@media (min-width: 1280px) {
	.media-section {
		.title {
			width: 960px;
		}
		.media-wrapper {
			min-height: 276px;
		}
		.pages {
			height: 276px;
			width: 960px;
			.section {
				height: 276px;
				width: 475px;
				.preview {
					height: 133px;
					width: 232px;
				}
			}
		}
	}
}

@media (min-width: 1440px) {
	.media-section {
		.title {
			font-size: 20px;
			letter-spacing: -0.4px;
			margin-bottom: 20px;
			width: 1080px;
		}
		.media-wrapper {
			min-height: 315px;
		}
		.pages {
			height: 315px;
			width: 1080px;
			.section {
				height: 315px;
				width: 532px;
				.preview {
					height: 150px;
					margin-bottom: 15px;
					width: 259px;
				}
			}
		}
	}
}

@media (min-width: 1600px) {
	.media-section {
		.title {
			width: 1200px;
		}
		.media-wrapper {
			min-height: 350px;
		}
		.pages {
			height: 350px;
			width: 1200px;
			.section {
				height: 350px;
				width: 592px;
				.preview {
					height: 167px;
					width: 289px;
				}
			}
		}
	}
}

@media (min-width: 1920px) {
	.media-section {
		.title {
			width: 1440px;
		}
		.media-wrapper {
			min-height: 420px;
		}
		.pages {
			height: 420px;
			width: 1440px;
			.section {
				height: 420px;
				width: 710px;
				.preview {
					height: 200px;
					margin-bottom: 20px;
					width: 345px;
				}
			}
		}
	}
}
</style>
